import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { LOCAL_ERROR_HANDLED, PASSIVE_REQUEST, USER_TRIGGERED_REQUEST } from './http-request-context';
import { UiErrorService } from './ui-error.service';

function isAuthFlowRequest(url: string): boolean {
  return url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/verify-email') || url.includes('/auth/refresh') || url.includes('/auth/logout');
}

function shouldShowUnexpectedError(error: HttpErrorResponse): boolean {
  return error.status === 0 || error.status === 403 || error.status >= 500;
}

function isConsentRequiredError(error: HttpErrorResponse): boolean {
  return (
    error.status === 403 &&
    (error.error as { code?: string } | null)?.code === 'privacy_consent_required'
  );
}

export function HttpErrorInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const uiErrorService = inject(UiErrorService);
  const authFlowRequest = isAuthFlowRequest(req.url);
  const userTriggeredRequest = req.context.get(USER_TRIGGERED_REQUEST);
  const localErrorHandled = req.context.get(LOCAL_ERROR_HANDLED);
  const passiveRequest = req.context.get(PASSIVE_REQUEST);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const consentRequiredError = isConsentRequiredError(error);

        if (consentRequiredError && !authFlowRequest && !passiveRequest) {
          authService.redirectToPrivacyConsent();
        } else {
          if (error.status === 401 && !authFlowRequest && !passiveRequest) {
            authService.logout();
          }

          if (userTriggeredRequest && !localErrorHandled && shouldShowUnexpectedError(error)) {
            uiErrorService.showUnexpectedHttpError(error.status);
          }
        }
      }

      return throwError(() => error);
    })
  );
}
